'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getIDType } from '@/lib/utils/commonUtils';
import ClassicModeLink from './ClassicModeLink';
import SearchBtn from '@/components/search/SearchBtn';
import Link from 'next/link';
import PageHeader from '@/components/PageHeader';
import { Flex, Select } from '@radix-ui/themes';
import { createRef } from 'react';
import SearchField from '@/components/search/SearchField';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import RecordCard from '@/components/search/RecordCard';
import { generateSearchQuery } from '@/lib/service/eidr-proxy-service';
import { generatePartySearchQuery, generateServiceSearchQuery } from "@/lib/service/eidr-rest-api-service";
import SessionState from "@/lib/session-state"
import { autorun } from "mobx"
import SignInPage from '@/app/auth/signin/page';


const registryOptions = [
    { value: 'content', name: 'Content', disabled: false },
    { value: 'party', name: 'Party', disabled: false },
    { value: 'service', name: 'Service', disabled: false },
];

const unauthRegistryOption = [
    { value: 'id', name: 'EIDR ID/Alt ID', disabled: false },
    { value: 'none', name: 'Sign In For More Options', disabled: true },
];

function LandingPage(): React.ReactElement {
    /**
     * Which Search Option is Currently Selected
     */
    const [searchOption, setSearchOption] = useState<string>('id');
    /**
     * The state of the current search value
     */
    const [searchValue, setSearchValue] = useState<string>(''); // This will hold the search value entered by the user
    /**
     * The state of the current placeholder text
     */
    const [placeholderText, setPlaceholderText] = useState<string>('');

    const [matchingRecords, setMatchingRecords] = useState([]);

    const [alert, setAlert] = useState<any>(null);

    const [lastSearchValue, setLastSearchValue] = useState<string>('');

    const form = createRef<HTMLFormElement>();
    const queryInput = createRef<HTMLInputElement>();

    /**
     * Is the user logged in?
     */
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(null);
    /**
     * Are we currently fetch data?
     */
    const [isLoading, setIsLoading] = useState(false);

    /**
     * What are the options to show in the registry dropdown
     */
    const [displayedOptions, setDisplayedOptions] =
        useState<any>(unauthRegistryOption);

    const router = useRouter();


    autorun(({ }) => {
        if (SessionState.isLoggedIn === true && !isLoggedIn) {
            setIsLoggedIn(true);
            setSearchOption('content');
            setDisplayedOptions(registryOptions);
        } else if (SessionState.isLoggedIn === false && (isLoggedIn === true || isLoggedIn === null)) {
            if (isLoggedIn !== false) {
                setIsLoggedIn(false);
                setSearchOption('id');
                setDisplayedOptions(unauthRegistryOption);
            }

        }
    }
    )


    /**
     * Bases on authentication state, show the appropriate placeholder text in the search box
     */
    useEffect(() => {
        //console.log('useEffect', 'displayedOptions', searchOption);

        if (isLoggedIn) {
            switch (searchOption) {
                case 'content':
                    setPlaceholderText('Search by Title or Resolve an EIDR ID or Alt ID');
                    break;
                case 'party':
                    setPlaceholderText(
                        'Search by Party Name or Resolve an EIDR ID', // or Alt ID
                    );
                    break;
                case 'service':
                    setPlaceholderText(
                        'Search by Service Name or Resolve an EIDR ID', // or Alt ID
                    );
                    break;
            }
        } else {
            setPlaceholderText('Resolve an EIDR Content, Service, or Party ID');
        }
    }, [displayedOptions, searchOption]);

    /**
     * When the search value is cleared,
     */
    useEffect(() => {
        //console.error('useEffect', 'searchValue', searchValue);
        if (searchValue === '') {
            setMatchingRecords([]);
        }
    }, [searchValue]);

    /**
     * When an alter is set, hide the alert after five seconds
     */
    useEffect(() => {
        if (alert) {
            setTimeout(() => {
                setAlert(null);
            }, alert.duration || 5000);
        }
    }, [alert]);

    /**
     * Keep track of the last search value, store in localstorage so we can show here next time the
     * user visits this page
     */
    useEffect(() => {
        let lastValue = getLastSearchValue();
        // console.log('useEffect, getting last searchValue', lastValue);
        setSearchValue(lastValue);
    }, [lastSearchValue]);

    /**
     * Registry select change
     * @param selectedValue
     */
    const handleRegistryOptionChange = (selectedValue: string) => {
        if (selectedValue) {
            setAlert(null);
            setSearchOption(selectedValue);
        }
    };

    /**
     * Search input change
     * @param value
     */
    const handleInputChange = (value) => {
        setSearchValue(value);
    };


    const onSearchValueChange = (value) => {
        setSearchValue(value);
        storeSearchValue(value);
        if (value === '') {
            sessionStorage.removeItem('searchInput');
        }
    };

    /**
     * If an entered value matches the registry search, we know the results returned
     * are either valid or invalid. As such, we'll redirect to the record view on success
     * @param id
     * @param registry
     */
    const resolve = async (id: string, registry: string) => {
        setIsLoading(true);
        let url = '/api/search/content?q=' + id;
        let response = fetch(url);
        try {
            const res = await response;
            let data = await res.json();

            if (Array.isArray(data?.results) && data?.results.length > 0) {
                data = data.results[0];
            }

            if (data.ID) {
                storeSearchValue(id);
                switch (registry) {
                    case "content":
                    case "shortDoi":
                        router.push('/content/' + data.ID);
                        break;
                    case "party":
                        router.push('/party/' + data.ID);
                        break;
                    case "service":
                        router.push('/service/' + data.ID);
                        break;
                }
            } else {
                setAlert({
                    title: 'No Results Found',
                    description: "It appears this ID doesn't match any records",
                });
            }
        } catch (e) {
        }
        setIsLoading(false);
    };

    /**
     * If the entered search term doesn't match any know registry pattern, we can assume either title
     * search or alternatedId search. Only logged-in users can search by title, so the api handler should
     * determine what to search for. If the results match an alternateId, we will show all matching alternate ID
     * records and show. If the search for a title produces results, we'll forward to the main search page for filtering, etc
     * @param {string} value
     * @param {string} searchOption
     */
    //tt0438407
    const search = async (value, searchOption) => {
        setIsLoading(true);
        let url: string;

        switch (searchOption) {
            case 'id':
            case 'content':
                url = '/api/search/content?q=' + value;
                break;
            case "service":
                url = '/api/search/service?q=' + value;
                break;
            case "party":
                url = '/api/search/party?q=' + value;
                break;
        }

        let response = fetch(url);

        try {
            const res = await response;
            let data = await res.json();

            if (data.totalMatches === 1 && data.results[0]) {

                let record = data.results[0];
                let registry = getIDType(record.ID);
                storeSearchValue(value);
                switch (registry) {
                    case 'content':
                        router.push('/content/' + record.ID);
                        break;
                    case 'party':
                        router.push('/party/' + record.ID);
                        break;
                    case 'service':
                        router.push('/service/' + record.ID);
                        break;
                }
            } else if (data.totalMatches > 1) {
                storeSearchValue(value);

                switch (searchOption) {
                    case 'id':
                    case 'content':
                        router.push('/search/content?q=' + JSON.stringify(generateSearchQuery(value)))
                        break;
                    case "service":
                        router.push('/search/service?q=' + JSON.stringify(generateServiceSearchQuery(value)))
                        break;
                    case "party":
                        router.push('/search/party?q=' + JSON.stringify(generatePartySearchQuery(value)))
                        break;
                }

            } else if (data.totalMatches === 0) {
                setAlert({
                    title: 'No Results Found',
                    type: 'info',
                });
            }
        } catch (e) {
            setAlert({
                title: 'Oops!',
                description: 'Something went wrong, please try again',
                type: 'error',
            });
        }
        setIsLoading(false);
    };

    const storeSearchValue = (value) => {
        if (
            value === undefined ||
            value === '' ||
            typeof value === 'undefined' ||
            value === 'undefined'
        ) {
            return;
        }
        sessionStorage.setItem('searchInput', value);
    };

    const getLastSearchValue = () => {
        return sessionStorage.getItem('searchInput');
    };

    const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!searchValue || searchValue === '') {
            let message = '';
            switch (searchOption) {
                case 'id':
                    //Search By Alternated ID
                    message = 'Please enter a valid Content, Party, or Service ID';
                    break;
                case 'content':
                    message =
                        'Please enter a valid Content ID, Alternated ID, or Content Title';
                    break;
                case 'service':
                    message =
                        'Please enter a valid Service ID, Alternated ID, or Service Name';
                    break;
                case 'party':
                    message =
                        'Please enter a valid Party ID, Alternated ID, or Party Name';
                    break;
            }
            setAlert({
                title: message,
                type: 'error',
            });
            return;
        }

        let registry = getIDType(searchValue);

        switch (registry) {
            case 'content':
                resolve(searchValue, registry).then();
                break;
            case 'party':
                resolve(searchValue, registry).then();
                break;
            case 'service':
                resolve(searchValue, registry).then();
                break;
            case 'shortDoi':
                resolve(searchValue, registry).then();
                break;
            default:
                if (isLoggedIn) {
                    switch (searchOption) {
                        case 'id':
                            //Search By Alternated ID
                            search(searchValue, searchOption).then();
                            break;
                        case 'content':
                            search(searchValue, searchOption).then();
                            break;
                        case 'service':
                            search(searchValue, searchOption).then();
                            break;
                        case 'party':
                            search(searchValue, searchOption).then();
                            break;
                    }
                } else {
                    setAlert({
                        title: 'That is not an EIDR ID',
                        description: (
                            <>
                                <Link href="/auth/signin" className="text-theme-blue underline">
                                    Log in
                                </Link>{' '}
                                for text-based Search.
                            </>
                        ),
                        type: 'warning',
                    });
                }

                break;
        }
    };

    return (
        <>
            <PageHeader title="Search"></PageHeader>
            <div className="mt-24 flex px-4">
                <div className="w-full flex justify-center">
                    <div className="w-5xl">
                        <form ref={form} onSubmit={onSubmit}>
                            <input type="hidden" name="query" ref={queryInput} />
                            <div className="flex md:flex-row flex-col w-full">
                                {isLoggedIn && (
                                    <div className="order-last md:order-first w-full md:w-1/4">
                                        <Select.Root
                                            defaultValue={searchOption}
                                            onValueChange={handleRegistryOptionChange}
                                            size="3"
                                            name="searchOption"
                                            value={searchOption}
                                        >
                                            <Flex
                                                style={{
                                                    paddingTop: '10px',
                                                    paddingBottom: '10px',
                                                    ['--select-trigger-height' as any]: '58px',
                                                    borderRadius: '0px 0px 0px 0px',
                                                    backgroundColor: 'var(--slate-2)',
                                                    width: '100%',
                                                }}
                                                asChild
                                            >
                                                <Select.Trigger />
                                            </Flex>
                                            <Select.Content className="w-full">
                                                {displayedOptions.map(({ name, value, disabled }) => (
                                                    <Select.Item
                                                        value={value}
                                                        key={`refTypeOption-${name}`}
                                                        disabled={disabled}
                                                    >
                                                        {name}
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Root>
                                    </div>
                                )}

                                <div className="order-first md:order-last w-full">
                                    <SearchField
                                        searchValue={searchValue}
                                        handleInputChange={handleInputChange}
                                        placeholderText={placeholderText}
                                        setSearchValue={onSearchValueChange}
                                        isLoading={isLoading}
                                    ></SearchField>
                                </div>
                            </div>
                        </form>

                        <div className="mt-2 flex justify-between items-center">
                            <ClassicModeLink />
                            {isLoggedIn && 0 ? <SearchBtn isAdvanced={false} /> : ''}
                            {!isLoggedIn && (
                                <div className="flex justify-end text-sm md:text-base">
                                    <Link
                                        type="button"
                                        className="text-theme-blue hover:underline"
                                        href="/auth/signin"
                                    >
                                        Sign in</Link>
                                    <span className="ml-1 text-theme-black">for Title search</span>
                                </div>
                            )}
                        </div>

                        {alert && (
                            <div className="flex flex-row justify-center">
                                <Alert variant="destructive" className="w-2lg mt-4">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>{alert.title}</AlertTitle>
                                    {alert.description && (<AlertDescription>{alert.description}</AlertDescription>)}
                                </Alert>
                            </div>
                        )}

                        {matchingRecords.length > 0 && (
                            <h4 className="text-lg my-3">
                                Your Search Returned Multiple Results
                            </h4>
                        )}

                        {matchingRecords.map((matchingRecord, i) => {
                            return (
                                <>
                                    <RecordCard
                                        key={'matching-record' + i}
                                        record={matchingRecord}
                                        records={[]}
                                        index={i}
                                    ></RecordCard>
                                </>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LandingPage;
